import './App.css';
import { useState, useEffect, useRef } from 'react';
import client from './sanityClient';
import imageUrlBuilder from '@sanity/image-url';
import ReaderView from './components/ReaderView';
import Gameboy from './components/Gameboy';
import useGameboy from './components/useGameboy';
import SnakeGame from './components/SnakeGame';

const builder = imageUrlBuilder(client);
function urlFor(source) {
  return builder.image(source);
}

function App() {
  const pages = ["Home", "About", "Projects", "Contact", "Game"];
  const [showIntro, setShowIntro] = useState(true);
  const [projectList, setProjectList] = useState([]);
  const [readerView, setReaderView] = useState(false);

  const {
    selectedIndex, setSelectedIndex,
    selectedProjectIndex, setSelectedProjectIndex,
    inPage, setInPage,
    viewingProjectDetails, setViewingProjectDetails,
    projectItemRefs,
    detailContentRef,
    fetchProjects,
  } = useGameboy(pages, projectList, setProjectList);

  const snakeGameApi = {
    moveUp: useRef(),
    moveDown: useRef(),
    moveLeft: useRef(),
    moveRight: useRef(),
    reset: useRef(),
  };

  const handleUp = () => {
    if (inPage) {
      if (pages[selectedIndex] === "Game") {
        snakeGameApi.moveUp.current();
      } else if (pages[selectedIndex] === "Projects" && viewingProjectDetails) {
        if (detailContentRef.current) detailContentRef.current.scrollBy({ top: -100, behavior: "smooth" });
      } else if (pages[selectedIndex] === "Projects") {
        setSelectedProjectIndex((prev) => {
          const newIndex = Math.max(0, prev - 1);
          const el = projectItemRefs.current[newIndex];
          if (el) el.scrollIntoView({ behavior: "smooth", block: "nearest" });
          return newIndex;
        });
      }
    } else {
      setSelectedIndex((prev) => {
        const newIndex = Math.max(0, prev - 1);
        return newIndex;
      });
    }
  };

  const handleDown = () => {
    if (inPage) {
      if (pages[selectedIndex] === "Game") {
        snakeGameApi.moveDown.current();
      } else if (pages[selectedIndex] === "Projects" && viewingProjectDetails) {
        if (detailContentRef.current) detailContentRef.current.scrollBy({ top: 100, behavior: "smooth" });
      } else if (pages[selectedIndex] === "Projects") {
        setSelectedProjectIndex((prev) => {
          const newIndex = Math.min(projectList.length - 1, prev + 1);
          const el = projectItemRefs.current[newIndex];
          if (el) el.scrollIntoView({ behavior: "smooth", block: "nearest" });
          return newIndex;
        });
      }
    } else {
      setSelectedIndex((prev) => {
        const newIndex = Math.min(pages.length - 1, prev + 1);
        return newIndex;
      });
    }
  };

  const handleLeft = () => {
    if (inPage && pages[selectedIndex] === "Game") {
      snakeGameApi.moveLeft.current();
    }
  };

  const handleRight = () => {
    if (inPage && pages[selectedIndex] === "Game") {
      snakeGameApi.moveRight.current();
    }
  };

  const handleA = () => {
    if (inPage) {
      if (pages[selectedIndex] === "Game") {
        snakeGameApi.reset.current();
      } else if (pages[selectedIndex] === "Projects" && !viewingProjectDetails) {
        setViewingProjectDetails(true);
        if (detailContentRef.current) detailContentRef.current.scrollTop = 0;
      }
    } else {
      setInPage(true);
      if (pages[selectedIndex] === "Projects" && projectList.length === 0) {
        fetchProjects();
      }
    }
  };

  const handleB = () => {
    if (viewingProjectDetails) {
      setViewingProjectDetails(false);
    } else if (inPage) {
      setInPage(false);
      setSelectedProjectIndex(0);
    }
  };

  const pageContent = {
    Home: (
      <div>
        <h2>Welcome</h2>
        <p>Hello! I'm a UX expert and game designer.</p>
        <p>Use the D-pad to explore my work.</p>
      </div>
    ),
    About: (
      <div>
        <h2>About Me</h2>
        <p>I'm Thomas Dielesen, a UX designer...</p>
      </div>
    ),
    Contact: (
      <div>
        <h2>Contact</h2>
        <p>Email: <a href="mailto:thomas@dielesen.eu">thomas@dielesen.eu</a></p>
        <p>LinkedIn: <a href="https://www.linkedin.com/in/thomas-dielesen-05b257234/">Profile</a></p>
        <p><a href="/ThomasDielesenCV.pdf">Download CV</a></p>
      </div>
    ),
    Game: <SnakeGame gameApi={snakeGameApi} />,
  };

  useEffect(() => {
    const timer = setTimeout(() => setShowIntro(false), 2500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (pages[selectedIndex] === "Projects" && projectList.length === 0) {
      fetchProjects();
    }
  }, [selectedIndex, pages, projectList, fetchProjects]);

  return (
    <div>
      {readerView ? (
        <ReaderView projects={projectList} urlFor={urlFor} setReaderView={setReaderView} />
      ) : (
        <Gameboy
          showIntro={showIntro}
          inPage={inPage}
          pages={pages}
          selectedIndex={selectedIndex}
          pageContent={pageContent}
          projectList={projectList}
          viewingProjectDetails={viewingProjectDetails}
          selectedProjectIndex={selectedProjectIndex}
          detailContentRef={detailContentRef}
          projectItemRefs={projectItemRefs}
          handleUp={handleUp}
          handleDown={handleDown}
          handleA={handleA}
          handleB={handleB}
          handleLeft={handleLeft}
          handleRight={handleRight}
          urlFor={urlFor}
          setReaderView={setReaderView}
        />
      )}
    </div>
  );
}

export default App;
